import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Input,
  List,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatMoneyValue } from '@/shared/lib/money'
import {
  applyLocalList,
  byNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import {
  ACTION_PERMISSION,
  StockMovementModal,
  type StockAction,
  type StockActionResult,
} from '@/features/inventory/StockMovementModal'
import { StockOperationAlert } from '@/features/inventory/StockOperationAlert'
import {
  fetchStockBalances,
  fetchStockItems,
  fetchStockValuation,
  fetchWarehouses,
  type StockBalance,
} from '@/features/inventory/stockApi'

interface Pending {
  action: StockAction
  nodeId: string | null
  itemId: string | null
}

interface Done {
  result: StockActionResult
  action: StockAction
}

/**
 * Qoldiqlar — OMBOR (tugun) kesimida.
 *
 * <h2>⚠️ OMBORLAR RO'YXATI SERVERDAN, IMKONIYAT BO'YICHA</h2>
 * `GET /api/inventory/stock/warehouses` faqat turida
 * `holds_stock = true` bo'lgan VA ko'rinish doirasidagi tugunlarni
 * qaytaradi (`QARORLAR.md` §19.3). Panelda `"WAREHOUSE"` satri YO'Q:
 * administrator "Markaziy sklad" degan yangi tur ochib unga ombor
 * imkoniyatini bersa, ro'yxat darhol uni ham ko'rsatadi.
 *
 * <h2>⚠️ QOLDIQ FAQAT HARAKAT ORQALI O'ZGARADI</h2>
 * Bu sahifada "qoldiqni tahrirlash" tugmasi YO'Q va bo'lmaydi ham.
 * `inventory.stock_balance` ni faqat trigger yozadi; qo'lda tuzatish
 * ham HARAKAT (inventarizatsiya) sifatida yoziladi va jurnalda iz
 * qoldiradi.
 *
 * <h2>⚠️⚠️ `0` — O'LCHANGAN QOLDIQ, «MA'LUMOT YO'Q» EMAS</h2>
 * Nol qoldiq bu yerda ODATIY hol: mol omborda bo'lgan va tugagan.
 * Shuning uchun miqdor katagi HECH QACHON "—" chizmaydi — chiziqcha
 * "o'lchanmagan" degani va ikkalasini bir xil ko'rsatish tugagan molni
 * "hisobda yo'q" bilan aralashtirardi. Farqning ikkinchi yarmi ham
 * aytiladi: qatori umuman bo'lmagan mol bu jadvalga TUSHMAYDI
 * (`fetchStockBalances` javadoc'i), ya'ni "ma'lumot yo'q" holati
 * bo'sh katak emas, YO'Q QATOR bo'lib ko'rinadi.
 *
 * <h2>⛔ SAHIFALASH YO'Q — SERVER UNI QO'LLAB-QUVVATLAMAYDI</h2>
 * `StockResource#balances` uchta parametr oladi: `nodeId`, `itemId`,
 * `belowMinOnly`. `page`/`size`/`sort`/`q` YO'Q. Uchala filtr ham
 * SERVERGA yuboriladi (pastdagi tanlagichlar), soxta sahifalash esa
 * yasalmadi (egasi qarori, 2026-08-20).
 *
 * <h2>⚠️ SUKUT BO'YICHA `created desc` YO'Q — MAYDONNING O'ZI YO'Q</h2>
 * `StockBalanceRow` da yaratilish vaqti umuman qaytmaydi (qator
 * trigger bilan tug'iladi va o'sha qatorning "yoshi" operator uchun
 * ma'no ham bermaydi). Vaqtni o'ylab topib saralash yolg'on tartib
 * bo'lardi, shuning uchun standart tartib SERVERNIKI qoladi va
 * boshqasi ustun sarlavhasidan tanlanadi.
 *
 * <h2>⚠️ ZAXIRA QIYMATI — MOLIYAVIY HISOBOT EMAS</h2>
 * U so'ralganda hisoblanadi, `ledger` ga hech narsa yozmaydi va unda
 * "yopilgan davr" tushunchasi yo'q (`StockValuation` javadoc'i,
 * `QARORLAR.md` §20.2). Ekranda bu OCHIQ aytiladi — aks holda
 * foydalanuvchi uni buxgalteriya hisoboti deb qabul qiladi.
 *
 * <h2>⛔ «YOPILGANLAR QOIDASI» BU SAHIFAGA TEGISHLI EMAS</h2>
 * Qoldiq qatorining «faol / yopilgan» holati YO'Q: u o'lchov, yozuv
 * emas. Uni molning `active` bayrog'i bilan bog'lash ikki xatoni
 * qo'shardi:
 * <ul>
 *   <li>nofaol molning omborda TURGAN qoldig'i ekrandan yo'qolardi —
 *       ya'ni hisobda bor zaxira «yo'q» bo'lib ko'rinardi, holbuki
 *       nofaol mol aynan hisobdan chiqarilishi kerak bo'lgan mol;</li>
 *   <li>`0` qoldiq «nofaol» deb o'qilardi — u esa ODATIY hol
 *       (yuqoridagi bo'lim).</li>
 * </ul>
 *
 * <p>⚠️ Shu sababdan mol tanlagichi `fetchStockItems(false)` bilan
 * to'ldiriladi va bu ATAYLAB: nofaol molni ro'yxatdan olib tashlasak,
 * uning qoldiq qatorlarini filtrlab ko'rishning yo'li qolmasdi
 * (`YOPILGAN-QOIDASI` ning tanlagichlar haqidagi bandi).
 *
 * <h2>⚠️ AMALLAR — IKKI HUQUQ, ALOHIDA</h2>
 * Kirim/chiqim/ko'chirish `inventory.stock.write`; inventarizatsiya va
 * hisobdan chiqarish `inventory.stock.adjust` (XAVFLI: kontragentsiz).
 */
export function StockBalancesPage() {
  const { t } = useTranslation()

  /**
   * ⚠️ FILTRLAR MANZILDA (`useTableUrlState`): sahifa yangilanganda
   * tanlangan ombor yo'qolmasin va "mana shu uch qator" havolasini
   * hamkasbga yuborish mumkin bo'lsin. Ilgari ular `useState` da
   * turardi — havolada ham, yangilashdan keyin ham yo'qolardi.
   *
   * ⚠️ Uchalasi ham SERVER filtri: qiymat so'rovga tushadi, panel
   * qatorlarni o'zi kesmaydi.
   */
  const list = useLocalListState()
  const nodeId = list.filter('node')
  const itemFilter = list.filter('item')
  const belowMinOnly = list.filter('belowMin') === 'yes'

  const [pending, setPending] = useState<Pending | null>(null)
  const [done, setDone] = useState<Done | null>(null)

  const warehouses = useQuery({
    queryKey: queryKeys.stock.warehouses,
    queryFn: fetchWarehouses,
    staleTime: 5 * 60_000,
  })

  const items = useQuery({
    queryKey: queryKeys.stock.items(false),
    queryFn: () => fetchStockItems(false),
    staleTime: 5 * 60_000,
  })

  const balances = useQuery({
    queryKey: queryKeys.stock.balances(nodeId, itemFilter, belowMinOnly),
    queryFn: ({ signal }) => fetchStockBalances(nodeId, itemFilter, belowMinOnly, signal),
  })

  const valuation = useQuery({
    queryKey: queryKeys.stock.valuation(nodeId),
    queryFn: ({ signal }) => fetchStockValuation(nodeId, signal),
  })

  const warehouseList = warehouses.data ?? []
  const itemList = items.data ?? []

  /** SERVER bergan qatorlar — ogohlantirish sanoqlari SHULARDAN */
  const serverRows = useMemo(() => (balances.data ? [...balances.data] : []), [balances.data])
  const belowMinCount = serverRows.filter((row) => row.belowMin).length

  /**
   * Ekranda ko'rinadigan qatorlar: matnli qidiruv va saralash.
   *
   * ⚠️ `belowMin` bu yerda TAKRORLANMAYDI — u serverning filtri va
   * shart bazadagi bayroq bilan aynan bir xil. Panelda ikkinchi ta'rif
   * yozilsa, ular bir kun ajralib ketardi (`StockBalanceRow` javadoc'i).
   */
  const rows = useMemo(
    () =>
      applyLocalList(serverRows, {
        search: list.search,
        searchFields: (row) => [row.name, row.sku, row.nodeName],
        filterState: list.state.filters,
        comparators: {
          item: byText((row) => row.name),
          warehouse: byText((row) => row.nodeName),
          quantity: byNumber((row) => row.quantity),
          minQuantity: byNumber((row) => row.minQuantity),
        },
        sort: list.sort,
      }),
    [serverRows, list.search, list.state.filters, list.sort],
  )

  const columns: ColumnsType<StockBalance> = [
    {
      // Sahifalanmagan ro'yxat — raqam to'g'ridan-to'g'ri `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'item',
      title: t('stock.field.item'),
      ...sortColumn(list.sort, 'item'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.sku}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'warehouse',
      title: t('stock.field.warehouse'),
      width: 200,
      ...sortColumn(list.sort, 'warehouse'),
      render: (_, row) => row.nodeName ?? '—',
    },
    {
      key: 'quantity',
      title: t('stock.field.quantity'),
      width: 180,
      ...sortColumn(list.sort, 'quantity'),
      render: (_, row) => (
        <Space size={6} wrap>
          {/*
            ⚠️ NOL ham SON: u xuddi qolganlari kabi chiziladi va
            chiziqchaga aylantirilmaydi. Rangi ham o'zgarmaydi —
            tugagan mol nosozlik emas, va qizil son operatorni
            mavjud bo'lmagan xatoni qidirishga yuborardi. Tooltip
            esa nol bilan "ma'lumot yo'q" farqini aytadi.
          */}
          {row.quantity === 0 ? (
            <Tooltip title={t('stock.balance.zeroHint')}>
              <Typography.Text strong>{`0 ${row.unit}`}</Typography.Text>
            </Tooltip>
          ) : (
            <Typography.Text strong>{`${String(row.quantity)} ${row.unit}`}</Typography.Text>
          )}
          {/*
            ⚠️ `belowMin` SERVERDA hisoblanadi va panel uni QAYTA
            hisoblamaydi: saqlangan bayroq eskirardi, ikkinchi hisob esa
            ikkinchi haqiqat manbai bo'lardi (`StockBalanceRow` javadoc'i).
          */}
          {row.belowMin ? (
            <Tooltip title={t('stock.balance.belowMinHint', { min: row.minQuantity, unit: row.unit })}>
              <Tag color="orange">{t('stock.balance.belowMin')}</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'minQuantity',
      title: t('stock.item.minQuantity'),
      width: 150,
      ...sortColumn(list.sort, 'minQuantity'),
      render: (_, row) =>
        row.minQuantity === 0 ? t('stock.item.minQuantityOff') : `${String(row.minQuantity)} ${row.unit}`,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        // Har amalning huquqi ALOHIDA qoladi (`ACTION_PERMISSION`) —
        // `RowActions` uni o'zi filtrlaydi, mantiq o'zgarmadi.
        <RowActions
          items={[
            {
              key: 'ISSUE',
              label: t('stock.action.ISSUE.short'),
              onClick: () => setPending({ action: 'ISSUE', nodeId: row.nodeId, itemId: row.itemId }),
              permission: ACTION_PERMISSION.ISSUE,
            },
            {
              key: 'TRANSFER',
              label: t('stock.action.TRANSFER.short'),
              onClick: () => setPending({ action: 'TRANSFER', nodeId: row.nodeId, itemId: row.itemId }),
              permission: ACTION_PERMISSION.TRANSFER,
            },
            {
              key: 'INTERNAL_USE',
              label: t('stock.action.INTERNAL_USE.short'),
              onClick: () => setPending({ action: 'INTERNAL_USE', nodeId: row.nodeId, itemId: row.itemId }),
              permission: ACTION_PERMISSION.INTERNAL_USE,
            },
            {
              key: 'ADJUSTMENT',
              label: t('stock.action.ADJUSTMENT.short'),
              onClick: () => setPending({ action: 'ADJUSTMENT', nodeId: row.nodeId, itemId: row.itemId }),
              permission: ACTION_PERMISSION.ADJUSTMENT,
              divider: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.stockBalances')}
        description={t('pageIntro.stockBalances')}
        extra={
          <Space wrap>
            <Can permission={ACTION_PERMISSION.RECEIPT}>
              <Button
                type="primary"
                onClick={() => setPending({ action: 'RECEIPT', nodeId, itemId: itemFilter })}
              >
                {t('stock.action.RECEIPT.short')}
              </Button>
            </Can>
            <Can permission={ACTION_PERMISSION.WRITE_OFF}>
              <Button
                danger
                onClick={() => setPending({ action: 'WRITE_OFF', nodeId, itemId: itemFilter })}
              >
                {t('stock.action.WRITE_OFF.short')}
              </Button>
            </Can>
          </Space>
        }
      />

      {/* ⚠️ Bu yerda AMAL xatosi uchun banner YO'Q va bo'lmasligi ham
          kerak: u oynaning ICHIDA ko'rsatiladi (`StockMovementModal`).
          Sahifaga chiqarilgan xabar modal niqobi ortida qolardi. */}
      {done?.result.kind === 'operation' ? (
        <StockOperationAlert
          operation={done.result.operation}
          action={done.action}
          onClose={() => setDone(null)}
        />
      ) : null}
      {done?.result.kind === 'transfer' ? (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setDone(null)}
          style={{ marginBottom: 16 }}
          message={t('stockTransfer.dispatched', {
            quantity: done.result.transfer.quantitySent,
            unit: done.result.transfer.unit,
            to: done.result.transfer.toNodeName,
          })}
          description={<Link to="/stock/transfers">{t('stockTransfer.openList')}</Link>}
        />
      ) : null}

      {balances.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(balances.error, t)}
        />
      ) : null}

      {/*
        ⚠️ So'rov YIQILGANDA "ombor yo'q" DEYILMAYDI.

        Shart `isSuccess`, `!isPending` EMAS (`useSiteIndex.reliable`
        bilan bir xil mulohaza): yiqilgan so'rovda ham `warehouseList`
        bo'sh bo'ladi va eski shart "sizda ombor yo'q" degan ISHONCHLI
        javobni chizardi — aslida javob umuman kelmagan. Xatoning o'zi
        pastda alohida ko'rsatiladi.
      */}
      {warehouses.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dataTable.loadFailed')}
          description={translateError(warehouses.error, t)}
          action={
            <Button size="small" onClick={() => void warehouses.refetch()}>
              {t('dataTable.retry')}
            </Button>
          }
        />
      ) : null}

      {/*
        ⚠️ Omborlar ro'yxati bo'sh bo'lsa sabab AYTILADI. "Ombor yo'q" va
        "sizning doirangizda ombor yo'q" bir xil ko'rinardi, va operator
        birinchisiga ishonardi.
      */}
      {warehouses.isSuccess && warehouseList.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('stock.balance.noWarehouses')}
          description={t('stock.balance.noWarehousesHint')}
        />
      ) : null}

      <Flex wrap gap="small" align="center" style={{ marginBottom: 16 }}>
        {/* ⚠️ Matnli qidiruv MIJOZDA va u sahifalanmagan, TO'LIQ
            ro'yxat ustida ishlaydi — serverda `q` parametri yo'q.
            Ombor va mol tanlovi esa SERVERGA boradi. */}
        <Input.Search
          allowClear
          value={list.searchText}
          onChange={(e) => list.setSearchText(e.target.value)}
          placeholder={t('stock.balance.searchPlaceholder')}
          style={{ width: 260, maxWidth: '100%' }}
        />
        <Select<string>
          allowClear
          showSearch
          optionFilterProp="label"
          value={nodeId}
          onChange={(value) => list.setFilter('node', value ?? null)}
          placeholder={t('stock.balance.allWarehouses')}
          style={{ width: 280 }}
          loading={warehouses.isPending}
          options={warehouseList.map((warehouse) => ({
            value: warehouse.id,
            label: `${warehouse.displayName} (${warehouse.code})`,
          }))}
        />
        <Select<string>
          allowClear
          showSearch
          optionFilterProp="label"
          value={itemFilter}
          onChange={(value) => list.setFilter('item', value ?? null)}
          placeholder={t('stock.balance.allItems')}
          style={{ width: 280 }}
          loading={items.isPending}
          options={itemList.map((item) => ({ value: item.id, label: `${item.name} · ${item.sku}` }))}
        />
        {/*
          Minimal qoldiq ogohlantirishining TORTIB olinadigan yarmi.
          Itarib yuboriladigani chiqim qilgan odamga tegadi
          (`StockOperation.lowStock`), bu esa o'sha paytda yonida
          turmagan ombor mudiri uchun.
        */}
        <Space size={8}>
          <Switch
            checked={belowMinOnly}
            onChange={(checked) => list.setFilter('belowMin', checked ? 'yes' : null)}
          />
          <Typography.Text>{t('stock.balance.belowMinOnly')}</Typography.Text>
        </Space>
        {list.narrowed ? (
          <>
            <Typography.Text type="secondary">
              {t('listView.shown', { shown: rows.length, total: serverRows.length })}
            </Typography.Text>
            <Button type="link" size="small" onClick={list.reset}>
              {t('dataTable.clearFilters')}
            </Button>
          </>
        ) : null}
      </Flex>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={t('stock.balance.listTitle', { count: serverRows.length })}>
            {/*
              ⚠️ FILTR YOQILGANDA XABAR BOSHQACHA. "3 qator chegaradan
              past" degan yozuv filtrlangan ro'yxatda ma'nosiz bo'lardi
              (u yerda HAMMA qator shunday), va undan yomoni — qisqargan
              ro'yxat "boshqa qoldiq yo'q" bo'lib o'qilardi. Filtr
              nimani KO'RSATMAYOTGANI ham aytiladi: qatori umuman
              bo'lmagan mol bu ro'yxatga tushmaydi (server izohi).

              ⚠️ Sanoq SERVER qatorlaridan (`serverRows`), ekrandagidan
              EMAS: matnli qidiruv ostida "1 qator chegaradan past"
              degan yozuv butun ombor holatini emas, qidiruv natijasini
              tasvirlardi.
            */}
            {belowMinOnly ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('stock.balance.belowMinFilterOn', { count: serverRows.length })}
                description={t('stock.balance.belowMinFilterHint')}
              />
            ) : belowMinCount > 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('stock.balance.belowMinSummary', { count: belowMinCount })}
                action={
                  <Button size="small" onClick={() => list.setFilter('belowMin', 'yes')}>
                    {t('stock.balance.belowMinShowOnly')}
                  </Button>
                }
              />
            ) : null}
            <Table<StockBalance>
              rowKey={(row) => `${row.nodeId}:${row.itemId}`}
              columns={columns}
              dataSource={rows}
              loading={balances.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 1000 }}
              onChange={localSortChange<StockBalance>(list.patch)}
              locale={{
                emptyText: balances.isError ? (
                  // ⚠️ Yiqilgan so'rov "qoldiq yo'q" bo'lib ko'rinmasin —
                  // xato o'zi tepada, bu yerda esa xulosa CHIQARILMAYDI
                  <EmptyState
                    variant="no-data"
                    title={t('dataTable.loadFailed')}
                    description={t('stock.balance.emptyUnknown')}
                  />
                ) : serverRows.length > 0 ? (
                  // ⚠️ Server qator berdi, lekin qidiruv/filtr ularni
                  // ko'rsatmadi — bu "qoldiq yo'q" EMAS va matn ham
                  // boshqa: yagona to'g'ri harakat — filtrni tozalash.
                  <EmptyState
                    variant="no-data"
                    title={t('listView.noMatch')}
                    description={t('listView.noMatchHint', { total: serverRows.length })}
                    actionLabel={t('dataTable.clearFilters')}
                    onAction={list.reset}
                  />
                ) : belowMinOnly ? (
                  // ⚠️ FILTR OSTIDA BO'SH RO'YXAT — YAXSHI XABAR, "qoldiq
                  // yo'q" emas: hech bir qator o'z chegarasidan pastga
                  // tushmagan. Eski matn ("Kirim yozing") bu yerda
                  // butunlay teskari maslahat bo'lardi.
                  <EmptyState
                    variant="no-data"
                    title={t('stock.balance.belowMinEmpty')}
                    description={t('stock.balance.belowMinEmptyHint')}
                  />
                ) : (
                  <EmptyState
                    variant="no-data"
                    title={t('stock.balance.empty')}
                    description={t('stock.balance.emptyHint')}
                  />
                ),
              }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {`${t('listView.fullListNote')} ${t('stock.balance.orderNote')}`}
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t('stock.valuation.title')}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('stock.valuation.notAccountingNote')}
            />
            <Statistic
              title={
                nodeId === null
                  ? t('stock.valuation.totalAll')
                  : t('stock.valuation.totalOne', {
                      name:
                        warehouseList.find((warehouse) => warehouse.id === nodeId)?.displayName ?? '',
                    })
              }
              loading={valuation.isPending}
              // ⚠️ `Money` — MINOR birlikdagi butun son. Bo'lish FAQAT
              // shu formatlagich ichida bo'ladi.
              value={formatMoneyValue(valuation.data?.total)}
            />
            {valuation.isError ? (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 16 }}
                message={translateError(valuation.error, t)}
              />
            ) : null}

            {/*
              ⚠️ MOL KESIMI — server uni ALLAQACHON yuboradi (`lines`) va
              u ekranda ko'rsatilmasdan tashlab yuborilardi. "Jami 12 mln"
              degan bitta son "qaysi mol qancha" degan savolga javob
              bermaydi, ya'ni operator uni tekshira olmasdi.

              ⚠️ Ro'yxat FILTRGA bo'ysunmaydi: `/valuation` faqat `nodeId`
              ni oladi (mol bo'yicha filtr yo'q). Shuning uchun u yerdagi
              mol tanlovi bu ro'yxatni QISQARTIRMAYDI va buni yozib
              qo'yish shart — aks holda "filtrladim, qiymat o'zgarmadi"
              degan savol tug'ilardi.
            */}
            {valuation.isSuccess && valuation.data.lines.length > 0 ? (
              <>
                <List
                  size="small"
                  style={{ marginTop: 16 }}
                  dataSource={[...valuation.data.lines]}
                  renderItem={(line) => (
                    <List.Item key={line.itemId}>
                      <Space direction="vertical" size={0}>
                        <Typography.Text>{line.name}</Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {`${line.sku} · ${String(line.quantity)} ${line.unit}`}
                        </Typography.Text>
                      </Space>
                      <Typography.Text strong>{formatMoneyValue(line.value)}</Typography.Text>
                    </List.Item>
                  )}
                />
                {itemFilter === null ? null : (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('stock.valuation.itemFilterIgnored')}
                  </Typography.Text>
                )}
              </>
            ) : null}

            <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
              {t('stock.valuation.methodNote')}
            </Typography.Paragraph>
          </Card>
        </Col>
      </Row>

      {/* ⚠️ `key` — amal yoki kontekst almashganda modal QAYTA quriladi:
          usiz oldingi amalning takroriy so'rov kaliti va maydonlari
          yangisiga o'tib qolardi. */}
      {pending === null ? null : (
        <StockMovementModal
          key={`${pending.action}:${pending.nodeId ?? ''}:${pending.itemId ?? ''}`}
          open
          action={pending.action}
          warehouses={warehouseList}
          items={itemList}
          presetNodeId={pending.nodeId}
          presetItemId={pending.itemId}
          onClose={() => setPending(null)}
          onDone={(result, action) => setDone({ result, action })}
        />
      )}
    </>
  )
}
